/** @jsx jsx */
import type { Interpolation } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import { DegreeEvent_AssignmentProgressState as AssignmentProgressState } from '__generated__/graphql-types';
import { flatten } from 'lodash';

import { SHORT_MONTH_DAY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import type { Theme } from '@coursera/cds-core';
import { Accordion, AccordionHeader, AccordionPanel, Button } from '@coursera/cds-core';
import { ChevronNextIcon } from '@coursera/cds-icons';

import { useDegreeContext } from 'bundles/degree-home/components/v2/DegreeHomeContext';
import { CalendarEmptyState } from 'bundles/degree-home/components/v3-dpm/components/Calendar/CalendarEmptyState';
import {
  getCalendarEventsWithOverdue,
  getCalendarKey,
  getOrderedCalendarListItems,
  scrollToNearestDateEvents,
} from 'bundles/degree-home/components/v3-dpm/components/Calendar/utils/index';

import _t from 'i18n!nls/degree-home';

import AcademicEventItem from './AcademicEventItem';
import { AssignmentEventItem } from './AssignmentEventItem';
import { LiveEventItem } from './LiveEventItem';
import { filterOptionValues } from './constants/constants';

const styles: Record<string, Interpolation<Theme>> = {
  calendarItemsContainer: css`
    height: 60vh;
    overflow-y: auto;
    padding: 1rem;
    margin: 0 0 1rem 0;
  `,

  dateHeader: css`
    position: sticky;
    top: -16px;
    border-radius: 4px;
    background: var(--cds-color-neutral-background-primary);

    button:focus {
      outline: inherit;
    }
  `,
};

const getNoDeadlineSummary = (filter: string, displayDate: string) => {
  if (filter === filterOptionValues.Deadlines) {
    return _t("You're up to date, you have no more deadlines for #{displayDate}.", { displayDate });
  }

  return _t("You're up to date, you have no more deadlines or live events for #{displayDate}.", { displayDate });
};

const getAriaSummaryForEmptyState = (filter: string, displayDate: string) => {
  if (filter === filterOptionValues.Overdue) {
    return _t("You're up to date, you have no more overdue items that were due on or after #{displayDate}", {
      displayDate,
    });
  }

  if (filter === filterOptionValues.LiveEvents) {
    return _t("You're up to date, you have no more live events for #{displayDate}", { displayDate });
  }

  return getNoDeadlineSummary(filter, displayDate);
};

export const CalendarItemsList = () => {
  const {
    calendarEvents,
    dateInFocus,
    overdueItemsCount,
    visibleCalendarEvents,
    filter,
    setFilter,
    calendarItemsLoading,
  } = useDegreeContext();
  const [scrollToNearestDateOnLoad, setScrollToNearestDateOnLoad] = useState(true);
  const [ariaCalendarSummary, setAriaCalendarSummary] = useState('loading calendar items');

  const calendarListItems = getCalendarEventsWithOverdue(calendarEvents, visibleCalendarEvents, filter);

  const calendarOrderedListItems = getOrderedCalendarListItems(dateInFocus, Object.keys(calendarListItems));

  // when filter is at All, count the number of dismissed overdue items and show button in overdue section
  const overdueSectionDismissedItemCount =
    filter === filterOptionValues.All && calendarListItems[filterOptionValues.Overdue]
      ? // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        overdueItemsCount - calendarListItems[filterOptionValues.Overdue].length
      : 0;
  const showViewMoreOverdueSectionButton = filter === filterOptionValues.All && overdueSectionDismissedItemCount !== 0;

  useEffect(() => {
    if (!scrollToNearestDateOnLoad || Object.keys(calendarListItems).length < 1) return;

    scrollToNearestDateEvents(dateInFocus, calendarListItems);
    setScrollToNearestDateOnLoad(false); // Set to false since we only need to scroll on the first data load
  }, [dateInFocus, scrollToNearestDateOnLoad, calendarListItems]);

  useEffect(() => {
    setScrollToNearestDateOnLoad(true);

    const getCalendarAriaSummary = () => {
      if (calendarItemsLoading) return '';

      const dateInFocusCalendarItems = calendarListItems[getCalendarKey(dateInFocus)] || [];
      const displayDate = formatDateTimeDisplay(dateInFocus, SHORT_MONTH_DAY_DISPLAY);

      // If there's a filter applied, get the amount of overdue items for the active filter
      const overdueCount =
        filter === 'all'
          ? overdueItemsCount
          : flatten(Object.values(visibleCalendarEvents || {})).filter(
              (item) =>
                item.__typename === 'DegreeEvent_AssignmentEvent' &&
                item.progressState === AssignmentProgressState.Overdue
            ).length;

      const dateInFocusSummary =
        dateInFocusCalendarItems?.length > 0
          ? _t('#{displayDate} has #{itemCount} events.', {
              displayDate,
              itemCount: dateInFocusCalendarItems.length,
            })
          : getNoDeadlineSummary(filter, displayDate);
      const overdueSummary = overdueCount
        ? _t('You have #{overdueCount} items overdue.', {
            overdueCount,
          })
        : '';

      /* Announce status message when there are no events and no overdue items */
      const ariaSummary =
        overdueCount || dateInFocusCalendarItems?.length > 0
          ? `${dateInFocusSummary} ${overdueSummary}`
          : getAriaSummaryForEmptyState(filter, displayDate);

      return ariaSummary;
    };
    setAriaCalendarSummary(getCalendarAriaSummary());
  }, [calendarItemsLoading, dateInFocus, filter, overdueItemsCount, visibleCalendarEvents, calendarListItems]);

  return (
    <div css={styles.calendarItemsContainer} id="calendarItemsContainer" data-testid="calendar-items-container">
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {/* Announces a summary of the calendar status when the date changes (instead of announcing each calendar item when the date changes)  */}
        {ariaCalendarSummary}
      </div>
      {calendarOrderedListItems.map((date) => (
        <div key={date} id={date}>
          <Accordion variant="silent" defaultExpanded={true}>
            <AccordionHeader
              css={styles.dateHeader}
              label={
                date === filterOptionValues.Overdue
                  ? _t('Overdue (#{overdueItemsCount})', { overdueItemsCount })
                  : formatDateTimeDisplay(date, SHORT_MONTH_DAY_DISPLAY)
              }
            />
            <AccordionPanel>
              {/*
               // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess */}
              {calendarListItems[date].map((item) => {
                if (item.__typename === 'DegreeEvent_AssignmentEvent') {
                  return <AssignmentEventItem key={item.assignmentUrl} item={item} />;
                } else if (item.__typename === 'DegreeEvent_LiveEvent') {
                  return <LiveEventItem key={item.eventUrl} item={item} />;
                } else if (item.__typename === 'DegreeEvent_AcademicEvent') {
                  return <AcademicEventItem key={item.id} academicEvent={item} />;
                } else {
                  return null;
                }
              })}
              {showViewMoreOverdueSectionButton && date === filterOptionValues.Overdue && (
                <Button
                  variant="ghost"
                  size="small"
                  css={css`
                    position: static;
                  `}
                  icon={<ChevronNextIcon />}
                  onClick={() => setFilter(filterOptionValues.Overdue)}
                >
                  {_t('View #{overdueSectionDismissedItemCount} more', { overdueSectionDismissedItemCount })}
                </Button>
              )}
            </AccordionPanel>
          </Accordion>
        </div>
      ))}
      <CalendarEmptyState />
    </div>
  );
};
